import { Dialog } from '@/components/Dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import useGeneratorStore from '@/state/generator-store';
import { cn } from '@/utils';
import { FolderIcon } from 'lucide-react';
import React from 'react';
import FolderDialog from '../FolderDialog';
import TrackNameInput from './TrackNameInput';
import { useController } from 'react-hook-form';
import { FolderSelectIcon } from '@/components/Icons';
import { useDimensions } from '@/hooks/useDimensions';

interface FolderSelectorCardProps {
  className?: string;
}

const FolderSelectorCard: React.FC<FolderSelectorCardProps> = ({ className }) => {
  const { control, formValues, generatorForm } = useGeneratorStore((state) => ({
    control: state.control,
    formValues: state.formValues,
    generatorForm: state.generatorForm,
  }));

  const { isIPhoneSEorSmaller } = useDimensions();

  const {
    field: { onChange: onFolderIdChange, value: formFolderId },
  } = useController({
    name: 'folderId',
    control,
    defaultValue: formValues?.folderId || null,
  });

  const [folderName, setFolderName] = React.useState('');

  const handleOnSelect = (selectedFolderId: number | null) => {
    generatorForm?.setValue('folderId', selectedFolderId);
    onFolderIdChange(selectedFolderId);
  };

  const showMoveDialog = () => {
    Dialog.info({
      closeClassName: 'left-auto right-4 !p-0 rounded-[4px] ',
      dialogContentClassName: 'bg-jen-content-primary-contrast h-[440px]',
      title: 'Select Folder For Your Track',
      contentJSX: ({ onClose }) => (
        <FolderDialog
          onSelect={() => {
            onClose();
          }}
          onClose={onClose}
          setFolderName={setFolderName}
          setFolderId={(id) => handleOnSelect(id as number)}
        />
      ),
    });
  };

  return (
    <div className={cn(className, isIPhoneSEorSmaller ? 'grid grid-cols-2 gap-4' : 'grid-cols-1')}>
      <TrackNameInput />
      <div className="relative flex items-center mt-2">
        <Input
          value={folderName}
          readOnly
          autoCorrect="off"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          onClick={() => showMoveDialog()}
          className="bg-gray-900 h-10 min-w-40 py-0 px-2 rounded-lg text-white text-[0.8rem] border-[#474A4C] font-pressura-mono cursor-pointer placeholder:text-jen-content-tertiary"
          placeholder="SELECT FOLDER"
          style={{
            background: 'linear-gradient(#191A1B, #1E2021)',
            boxShadow: 'inset 2px 2px 12px rgba(0, 0, 0, 1)',
          }}
        />
        <Button
          variant="ghost"
          size="icon"
          type="button"
          className="absolute right-2 top-1/2 transform -translate-y-1/2 hover:bg-transparent"
          onClick={() => showMoveDialog()}
        >
          <span className="absolute z-0 mt-4 ml-4">
            <FolderSelectIcon />
          </span>
          <FolderIcon className="h-4 w-4 text-gray-400 z-10" />
        </Button>
      </div>
    </div>
  );
};

export default FolderSelectorCard;
